import { SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { OffsiteCampaignStatus } from 'models/offsite/Campaign/LifeCycle';
import { Template } from 'models/template/Template';
import { Moment } from 'moment-timezone';

export enum TargetAudienceType {
  BasketAbandonment = 'cartAbandonment',
  BrowseAbandonment = 'browseAbandonment',
  Purchase = 'purchase',
}

export enum TargetLookbackPeriod {
  Days = 'd',
  Months = 'M',
}

export interface TargetAudience {
  audience: TargetAudienceType;
  days: number;
  dateType?: TargetLookbackPeriod;
  startDate?: Moment;
  endDate?: Moment;
  isUsingDateRange?: boolean;
}

export interface CampaignEditorState {
  status: OffsiteCampaignStatus.Draft | OffsiteCampaignStatus.Scheduled | OffsiteCampaignStatus.Triggered;
  hasUnsavedChanges: boolean;
  drawerOpen: boolean;
  readOnly?: boolean;
  campaign: {
    name: string;
    targetAudience: {
      audience: TargetAudienceType;
      days: number;
      dateType?: TargetLookbackPeriod;
      startDate?: Moment;
      endDate?: Moment;
      isUsingDateRange?: boolean;
    }[];
    additionalSegmentation: { [key in TargetAudienceType]?: SegmentationRule<any, any, any>[][] };
    enablePurchaseCheck: { [key in TargetAudienceType]?: boolean };
    purchaseCheckDays: { [key in TargetAudienceType]?: number };
    purchaseCheck?: { [key in TargetAudienceType]?: PurchaseCheckState };
    ids: {
      targetAudience: { [key in TargetAudienceType]?: string };
      segmentId: { [key in TargetAudienceType]?: string };
      percentageSplitTestId: { [key in TargetAudienceType]?: string };
      cycleId: { [key in TargetAudienceType]?: string };
      purchaseCheckId: { [key in TargetAudienceType]?: string };
    };
    bulkSortByFeature: boolean;
    sendDate?: Date;
    template?: { [key in TargetAudienceType]?: Template };
    subjectLine?: { [key in TargetAudienceType]?: { id: string; label: string } };
    maxSendDate?: Moment;
    minSendDate?: Moment;
  };
}

export type CampaignEditorAction =
  | { type: 'SET_CAMPAIGN_NAME'; payload: { name: string } }
  | {
      type: 'SET_TARGET_AUDIENCE';
      payload: TargetAudience & { index: number };
    }
  | {
      type: 'ADD_TARGET_AUDIENCE';
    }
  | {
      type: 'DELETE_TARGET_AUDIENCE';
      payload: TargetAudience & { index: number };
    }
  | {
      type: 'SET_ADDITIONAL_SEGMENTATION';
      payload: { [key in TargetAudienceType]?: SegmentationRule<any, any, any>[][] };
    }
  | { type: 'SET_PURCHASE_CHECK'; payload: { [key in TargetAudienceType]?: boolean } }
  | { type: 'SET_PURCHASE_CHECK_DAYS'; payload: { [key in TargetAudienceType]?: number } }
  | { type: 'SET_PURCHASE_CHECK_ALL'; payload: PurchaseCheckData }
  | { type: 'SET_SEND_DATE'; payload: Date }
  | { type: 'SET_TEMPLATE'; payload: { [key in TargetAudienceType]?: Template } }
  | { type: 'SET_SUBJECT_LINE'; payload: { [key in TargetAudienceType]?: { id: string; label: string } } }
  | { type: 'SET_DRAWER_OPEN'; payload: boolean }
  | { type: 'SET_SORT_OPTION'; payload: boolean }
  | { type: 'SET_STATUS'; payload: CampaignEditorState['status'] }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean };

export enum PurchaseCheckUnit {
  Days = 'days',
  Months = 'months',
}

export interface PurchaseCheckState {
  enablePurchaseCheck: boolean;
  purchaseCheckDays: number;
  purchaseCheckMonths: number;
  purchaseCheckUnit: PurchaseCheckUnit;
  purchaseCheckStartDate: Moment | null;
  purchaseCheckEndDate: Moment | null;
  isPurchaseCheckUsingDateRange: boolean;
}

export interface PurchaseCheckData {
  targetAudience: TargetAudience;
  enablePurchaseCheck: boolean;
  purchaseCheckDays: number;
  purchaseCheckMonths: number;
  purchaseCheckUnit: PurchaseCheckUnit;
  purchaseCheckStartDate: Moment | null;
  purchaseCheckEndDate: Moment | null;
  isPurchaseCheckUsingDateRange: boolean;
}
